/**
 * Utilities for direct profile navigation — input normalisation and validation.
 *
 * Kept separate from React components so Vite Fast Refresh never gets confused
 * by mixing module-level helpers with component definitions.
 */

/**
 * Attempt to extract a profile identifier from a Bluesky profile URL.
 *
 * Handles:
 *   https://bsky.app/profile/handle.bsky.social
 *   https://bsky.app/profile/did:plc:abc123
 *   https://bsky.app/profile/handle/post/rkey  (strip post path)
 *
 * Returns null if the URL doesn't match a Bluesky profile pattern.
 */
export function extractBlueskyProfileIdentifier(url: string): string | null {
    try {
        const parsed = new URL(url)
        if (parsed.hostname !== 'bsky.app') return null
        const segments = parsed.pathname.split('/').filter(Boolean)
        // Expect: /profile/<identifier>[/post/<rkey>]
        if (segments[0] !== 'profile' || !segments[1]) return null
        return segments[1]
    } catch {
        return null
    }
}

/**
 * Normalise raw input from the "Go to profile" sheet into a profile identifier.
 *
 * Returns the normalised identifier string (still needs route-level validation),
 * or null if the input is empty after trimming.
 *
 * Transformation rules:
 *   - Trim surrounding whitespace
 *   - Strip a single leading '@' (e.g. @handle.bsky.social → handle.bsky.social)
 *   - For Bluesky URLs, extract the identifier segment from the path
 *   - Everything else is returned as-is for the profile route to handle
 */
export function normaliseProfileInput(raw: string): string | null {
    const trimmed = raw.trim()
    if (!trimmed) return null

    // Bluesky URL?
    const fromUrl = extractBlueskyProfileIdentifier(trimmed)
    if (fromUrl) return fromUrl

    // Strip leading @ (handles and AP handles)
    return trimmed.replace(/^@/, '')
}

/**
 * Validate that a normalised identifier is structurally acceptable before
 * attempting navigation. This is a lightweight client-side check only —
 * profile-not-found is handled at the profile page level.
 *
 * Accepts:
 *   - did:plc:... / did:web:...  (DID)
 *   - user@domain.tld            (ActivityPub handle)
 *   - subdomain.tld+ handle      (AT Protocol handle — must contain a dot, no slashes)
 */
export function isValidProfileIdentifier(identifier: string): boolean {
    if (!identifier) return false

    // DID: must start with "did:" and have content after the scheme
    if (identifier.startsWith('did:')) {
        return identifier.length > 4
    }

    // Reject anything that looks like a URL scheme (e.g. http://, https://)
    if (/^https?:\/\//i.test(identifier)) return false

    // Reject strings containing slashes (URLs that weren't extracted)
    if (identifier.includes('/')) return false

    // ActivityPub handle: contains @ in the middle
    const atIdx = identifier.indexOf('@')
    if (atIdx > 0 && atIdx < identifier.length - 1) return true

    // AT Protocol handle: domain-style (must contain at least one dot)
    return identifier.includes('.')
}
